import { Navigate, useLocation } from 'react-router-dom'
import { useMfaStatus } from '../api/useMfa'
import { useViewer } from '../api'

/**
 * Gate for staff-only areas: a staff member who has not set up two-factor is
 * sent to /2fa and cannot get past it until a device is enrolled and confirmed.
 *
 * This mirrors the server rule rather than replacing it. The API refuses these
 * requests on its own (error 4030 from the staff permission classes), so a user
 * who edits the URL or disables JavaScript gains nothing; this exists so the
 * refusal is a usable screen instead of a failed request.
 *
 * Players are unaffected -- is_staff_account is false for them, so they fall
 * straight through.
 */
export const RequireMfa = ({ children }) => {
    const location = useLocation()
    const { data: viewer } = useViewer()
    const { data: status, isLoading, isError } = useMfaStatus(Boolean(viewer?.is_auth))

    // Wait rather than flash the page: rendering children first and redirecting
    // afterwards would briefly show Team Tools to someone who must not see it.
    if (isLoading || (!status && !isError)) return null

    // If the status call itself failed we cannot prove the session is verified,
    // so fail closed and send them to the 2FA screen, which reports the error.
    if (isError) return <Navigate to='/2fa' replace />

    // The server's own answer to "would this be refused", which is the
    // question this gate exists to anticipate. Reading session_verified alone
    // sent staff to enrolment even when MFA was switched off server-side, so
    // the gate refused what the API was happily allowing. Falls back to the
    // old pair for an older server that does not send the field.
    if (status.verification_required === false) return children
    if (!status.is_staff_account) return children
    if (status.session_verified) return children

    // Carry the intended destination so enrolment can return them to it.
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/2fa?next=${next}`} replace />
}
